/**
 * Les routes `/stages/commune/:slug` et `/stages/vacances/:slug`.
 *
 * Elles récupèrent tout le catalogue puis filtrent en mémoire, plutôt que de
 * demander à la base un sous-ensemble. Il y a quelques dizaines de lignes
 * publiques : une seconde requête par page coûterait plus que le tri, et la
 * page a de toute façon besoin de la liste complète pour proposer ses voisines.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { stagesPublics } from '../../lib/seo/gateway.supabase';
import { ORGANISATEUR, filDArianeJsonLd, type StagePublic } from '../../lib/seo/stage';
import {
  BASE,
  baliseTitre,
  descriptionDeLaPage,
  filDArianeDeLaPage,
  introductionDeLaPage,
  regroupements,
  titreDeLaPage,
  titreDesVoisines,
  type Genre,
} from '../../lib/seo/liste';
import { ORIGINE, useEnTete } from '../../lib/seo/enTete';
import ListeVue from './ListeVue';

interface Props {
  genre: Genre;
}

const ListePubliquePage = ({ genre }: Props) => {
  const { slug = '' } = useParams();
  const [stages, setStages] = useState<StagePublic[]>([]);
  const [chargement, setChargement] = useState(true);

  useEffect(() => {
    let vivant = true;
    stagesPublics()
      .then((s) => {
        if (vivant) setStages(s);
      })
      .catch(() => {
        if (vivant) setStages([]);
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, []);

  const tous = useMemo(() => regroupements(genre, stages), [genre, stages]);
  const courant = useMemo(() => tous.find((r) => r.slug === slug), [tous, slug]);

  const donnees = useMemo(
    () =>
      filDArianeJsonLd(
        [
          ...filDArianeDeLaPage(),
          { nom: courant?.nom ?? slug, chemin: `${BASE[genre]}/${slug}` },
        ],
        ORIGINE,
      ),
    [courant, genre, slug],
  );

  useEnTete({
    titre: courant
      ? baliseTitre(genre, courant)
      : `Page introuvable | Éclosion`,
    description: courant
      ? descriptionDeLaPage(genre, courant)
      : `${ORGANISATEUR} organise des stages et plaines de vacances pour enfants à Bruxelles.`,
    chemin: `${BASE[genre]}/${slug}`,
    /*
      Une page sans regroupement correspondant n'a rien à indexer. Elle garde
      ses liens — c'est ce que `follow` dit — pour qu'un robot arrivé là par
      une vieille adresse reparte vers le catalogue plutôt que dans le vide.
    */
    indexable: Boolean(courant),
    donnees,
  });

  if (chargement) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!courant) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-3">
          {genre === 'commune'
            ? 'Aucune activité dans cette commune pour le moment'
            : 'Aucune activité pour cette période'}
        </h1>
        <p className="text-gray-600 mb-6">
          {ORGANISATEUR} organise des plaines de vacances et des stages à thème toute
          l’année à Bruxelles.
        </p>
        <Link to="/stages" className="btn-primary">
          Voir tout le catalogue
        </Link>
      </div>
    );
  }

  return (
    <ListeVue
      titre={titreDeLaPage(genre, courant)}
      introduction={introductionDeLaPage(genre, courant)}
      stages={courant.stages}
      filDAriane={filDArianeDeLaPage()}
      voisines={{
        titre: titreDesVoisines(genre),
        liens: tous.filter((r) => r.slug !== courant.slug),
        base: BASE[genre],
      }}
    />
  );
};

export default ListePubliquePage;
